import React, { useState, useRef } from 'react';
import { Button, Spinner, Overlay, Popover, ListGroup, Alert } from 'react-bootstrap';
import { useExport, EXPORT_CONFIGS, EXPORT_FORMAT_OPTIONS} from '../hooks/use-export';

interface ExportPopoverProps {
  exportConfig: keyof typeof EXPORT_CONFIGS | { endpoint: string; baseFilename: string; timeout?: number };
  searchQuery?: string;
  filters?: Record<string, string>;
  dateRange?: { startDate?: string; endDate?: string };
  disabled?: boolean;
  availableFormats?: string[];
  onExportStart?: () => void;
  onExportComplete?: () => void;
  onExportError?: (error: string) => void;
}

const ExportPopover: React.FC<ExportPopoverProps> = ({ 
  exportConfig,
  searchQuery,
  filters,
  dateRange,
  disabled = false,
  availableFormats = ['csv', 'xlsx', 'pdf'],
  onExportStart,
  onExportComplete,
  onExportError
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const targetRef = useRef<HTMLButtonElement>(null);

  const config = typeof exportConfig === 'string' ? EXPORT_CONFIGS[exportConfig] : exportConfig;
  
  const { isExporting, exportData, exportError, clearError } = useExport(config);

  const availableOptions = EXPORT_FORMAT_OPTIONS.filter(option => 
    availableFormats.includes(option.format)
  );

  const handleExportClick = async (format: string) => {
    setShowPopover(false);
    clearError();
    
    try {
      onExportStart?.();
      
      await exportData({
        format,
        searchQuery,
        filters,
        dateRange
      });
      
      onExportComplete?.();
    } catch (error) {
      onExportError?.(error instanceof Error ? error.message : 'Export failed');
    }
  };

  const togglePopover = () => {
    if (!disabled && !isExporting) {
      setShowPopover(!showPopover);
    }
  };

  return (
    <>
      <Button
        ref={targetRef}
        className="custom-button export-btn"
        onClick={togglePopover}
        disabled={disabled || isExporting}
      >
        {isExporting ? (
          <>
            <Spinner animation="border" size="sm" className="me-1" />
            Exporting...
          </>
        ) : (
          <>
            Export
          </>
        )}
      </Button>

      <Overlay
        target={targetRef.current}
        show={showPopover}
        placement="bottom-end"
        onHide={() => setShowPopover(false)}
        rootClose
      >
        <Popover id="export-popover" className="export-popover">
          <Popover.Header as="h6" className="py-2">
            Choose Export Format
          </Popover.Header>
          
          <Popover.Body className="p-0">
            {exportError && (
              <Alert variant="danger" className="m-2 py-2" dismissible onClose={clearError}>
                <small>{exportError}</small>
              </Alert>
            )}
            
            <ListGroup variant="flush">
              {availableOptions.map((option) => (
                <ListGroup.Item
                  key={option.format}
                  action
                  onClick={() => handleExportClick(option.format)}
                  className="d-flex align-items-center export-option"
                >
                  <div className="fw-medium">{option.label}</div>
                </ListGroup.Item>
              ))}
            </ListGroup>
            
            {(searchQuery || filters || dateRange) && (
              <div className="px-3 py-2 bg-light border-top">
                <small className="text-muted">
                  {searchQuery && "Search filters applied"}
                  {filters && Object.keys(filters).length > 0 && " • Additional filters applied"}
                  {dateRange && (dateRange.startDate || dateRange.endDate) && " • Date range applied"}
                </small>
              </div>
            )}
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
};

export default ExportPopover;